import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@nexelem/deprecated-theme';
import { cn } from '@nexelem/utils';
import { VariantProps, cva } from 'class-variance-authority';
import clsx from 'clsx';
import { ComponentProps, ReactNode, forwardRef } from 'react';

const iconSlotVariants = cva(
  'pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center justify-center text-icon-primary group-focus-within:!text-icon-focus group-hover:text-icon-hover',
  {
    variants: {
      size: {
        small: 'left-1 min-h-6 w-6',
        default: 'left-1 min-h-6 w-6',
        jumbo: 'left-3 min-h-6 w-6',
        extraJumbo: 'left-3 min-h-8 w-8 *:min-h-6 *:min-w-6',
      },
      disabled: {
        true: 'group-hover:text-icon-primary',
      },
      error: {
        true: 'text-icon-error',
      },
      readOnly: {
        true: 'group-focus-within:!text-icon-primary group-hover:text-icon-primary',
      },
    },
  },
);

const inputVariants = cva(
  'box-border block w-full cursor-text resize-none rounded-xs border-0 bg-surface-primary outline outline-border-primary placeholder:pr-0.5 placeholder:italic placeholder-shown:truncate read-only:cursor-default read-only:outline-0 hover:outline-border-hover focus:read-only:outline-none focus-visible:outline-2 focus-visible:outline-border-focus disabled:cursor-not-allowed disabled:bg-surface-disabled disabled:text-text-secondary-darker disabled:outline-border-disabled',
  {
    variants: {
      // Autoadjust height based on content, textarea without rows prop
      sizingContent: {
        true: 'field-sizing-content',
      },
      size: {
        small: 'px-2 py-0.75 text-s18',
        default: 'px-2 py-2.25 text-s18',
        jumbo: 'px-4 py-5.75 text-s18',
        extraJumbo: 'px-4 py-8.25 text-s24',
      },
      icon: {
        small: 'pl-7.5',
        default: 'pl-7.5',
        jumbo: 'pl-10',
        extraJumbo: 'pl-12',
      },
      endIconCount: {
        1: 'pr-9',
        2: 'pr-17',
      } as Record<number, string>,
      error: {
        true: 'outline-2 outline-border-state-error-dark',
      },
    },
  },
);

type InputProps = {
  size?: VariantProps<typeof inputVariants>['size'];
  error?: boolean;
  StartIcon?: ReactNode;
  EndIcon?: ReactNode;
  onClear?: () => void;
  hasClearButton?: boolean;
  inputClassName?: string;
  /** Use textarea */
  multiline?: boolean;
  /** Rows for textarea, only when multiline */
  rows?: number;
} & Omit<ComponentProps<'input'>, 'size'>;

const InputSlot = styled('input')(() => null);

/**
 * Base styled Input component, with support for clear button, icon, and end icon.
 *  */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    size = 'default',
    disabled,
    error,
    StartIcon,
    className,
    onClear,
    hasClearButton,
    readOnly,
    inputClassName,
    EndIcon,
    multiline,
    ...restProps
  } = props;

  const clearButton = hasClearButton && !readOnly;

  const endIconCount = [clearButton, EndIcon].filter(Boolean).length;

  return (
    <div className={clsx('group relative', className)}>
      {StartIcon && <span className={cn(iconSlotVariants({ size, disabled, error, readOnly }))}>{StartIcon}</span>}
      <InputSlot
        aria-invalid={error}
        as={multiline ? 'textarea' : 'input'}
        disabled={disabled}
        readOnly={readOnly}
        ref={ref}
        className={cn(
          inputVariants({
            size,
            icon: StartIcon ? size : undefined,
            endIconCount,
            error,
            sizingContent: !props.rows && multiline,
          }),
          inputClassName,
        )}
        {...restProps}
      />
      {endIconCount > 0 && (
        <span
          className={clsx(
            'absolute top-0 box-border flex h-full items-center justify-between px-2',
            endIconCount === 2 ? 'w-17' : 'w-9',
            size === 'extraJumbo' ? 'right-2' : 'right-0',
          )}
        >
          {clearButton && !readOnly && (
            <FontAwesomeIcon
              icon={faCircleXmark}
              className={clsx(
                'cursor-pointer text-icon-primary hover:text-icon-hover active:text-icon-action',
                size === 'extraJumbo' ? 'min-h-6 w-6' : 'min-h-5 w-5',
              )}
              onClick={onClear}
            />
          )}
          {EndIcon && <span>{EndIcon}</span>}
        </span>
      )}
    </div>
  );
});

Input.displayName = 'Input';
